import React from 'react';
// Mock data for the application
export type ListingStatus = 'pending' | 'approved' | 'rejected';
export type UserRole = 'owner' | 'admin' | 'user';
export interface Comment {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  rating: number;
  comment: string;
  createdAt: string;
}
export interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  location: string;
  type: 'house' | 'land';
  listingType: 'sale' | 'rent';
  rentPeriod?: 'monthly' | 'yearly';
  size: number;
  bedrooms?: number;
  bathrooms?: number;
  images: string[];
  documents: {
    title: string;
    url: string;
  }[];
  owner: {
    id: string;
    name: string;
    email: string;
    phone: string;
    companyName?: string;
    companyNIF?: string;
    companyLogo?: string;
  };
  featured?: boolean;
  createdAt: string;
  comments: Comment[];
  averageRating: number;
  totalRatings: number;
  status: ListingStatus;
  rejectionReason?: string;
  reviewedAt?: string;
  reviewedBy?: string;
}
export interface Visit {
  id: string;
  listingId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  date: string;
  time: string;
  message: string;
  status: 'pending' | 'accepted' | 'rejected';
}
export interface License {
  id: string;
  planName: string;
  maxListings: number;
  price: number;
  duration: number;
  features: string[];
}
export interface UserLicense {
  licenseId: string;
  purchaseDate: string;
  expiryDate: string;
  usedListings: number;
  maxListings: number;
  isActive: boolean;
}
export interface User {
  id: string;
  name: string;
  email: string;
  password: string;
  phone: string;
  role: UserRole;
  companyName?: string;
  companyNIF?: string;
  companyLogo?: string;
  license?: UserLicense;
  createdAt: string;
  isActive: boolean;
}
// Format currency helper function
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};
// Format area helper function
export const formatArea = (sqft: number): string => {
  return `${sqft.toLocaleString()} sq ft`;
};
// Format date helper function
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};
// Calculate average rating
export const calculateAverageRating = (comments: Comment[]): number => {
  if (comments.length === 0) return 0;
  const sum = comments.reduce((acc, comment) => acc + comment.rating, 0);
  return Math.round(sum / comments.length * 10) / 10;
};
// Check if license is expired
export const isLicenseExpired = (expiryDate: string): boolean => {
  const now = new Date();
  const expiry = new Date(expiryDate);
  return now > expiry;
};
// Get days remaining until a date
export const getDaysRemaining = (dateString: string): number => {
  const now = new Date();
  const target = new Date(dateString);
  const diffTime = target.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};
// Admin credentials
export const ADMIN_CREDENTIALS = {
  email: 'admin@kredita.com',
  password: 'Kredita@Admin2024',
  username: 'admin'
};
// Mock users database
export const mockUsers: User[] = [{
  id: 'admin-001',
  name: 'Admin User',
  email: 'admin@kredita.com',
  password: 'Kredita@Admin2024',
  phone: '(555) 000-0000',
  role: 'admin',
  createdAt: '2023-01-01T00:00:00Z',
  isActive: true
}, {
  id: '101',
  name: 'John Smith',
  email: 'john@example.com',
  password: 'password123',
  phone: '(123) 456-7890',
  role: 'owner',
  companyName: 'Prime Real Estate Agency',
  companyNIF: '123456789',
  companyLogo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  license: {
    licenseId: 'starter',
    purchaseDate: '2024-01-01T00:00:00Z',
    expiryDate: '2024-01-31T23:59:59Z',
    usedListings: 3,
    maxListings: 5,
    isActive: true
  },
  createdAt: '2023-05-01T00:00:00Z',
  isActive: true
}, {
  id: '102',
  name: 'Sarah Johnson',
  email: 'sarah@example.com',
  password: 'password123',
  phone: '(123) 456-7891',
  role: 'owner',
  license: {
    licenseId: 'professional',
    purchaseDate: '2024-01-01T00:00:00Z',
    expiryDate: '2024-02-01T23:59:59Z',
    usedListings: 2,
    maxListings: 15,
    isActive: true
  },
  createdAt: '2023-06-15T00:00:00Z',
  isActive: true
}, {
  id: '103',
  name: 'Michael Brown',
  email: 'michael@example.com',
  password: 'password123',
  phone: '(123) 456-7892',
  role: 'owner',
  createdAt: '2023-07-01T00:00:00Z',
  isActive: true
}];
// License plans
export const licensePlans = [{
  id: 'starter',
  planName: 'Starter',
  price: 29.99,
  maxListings: 5,
  features: ['Up to 5 property listings', 'Basic property details', 'Photo uploads (up to 10 per listing)', 'Document uploads (up to 5 per listing)', 'Standard customer support']
}, {
  id: 'professional',
  planName: 'Professional',
  price: 59.99,
  maxListings: 15,
  features: ['Up to 15 property listings', 'Advanced property details', 'Photo uploads (up to 20 per listing)', 'Document uploads (unlimited)', 'Featured listings (2 per month)', 'Priority customer support']
}, {
  id: 'enterprise',
  planName: 'Enterprise',
  price: 99.99,
  maxListings: 50,
  features: ['Up to 50 property listings', 'Premium property details', 'Photo uploads (unlimited)', 'Document uploads (unlimited)', 'Featured listings (5 per month)', 'Premium placement in search results', 'Dedicated account manager', '24/7 priority support']
}];
// Mock visit requests
export const mockVisits: Visit[] = [{
  id: '1',
  listingId: '1',
  userId: 'u1',
  userName: 'Alice Johnson',
  userEmail: 'alice@example.com',
  userPhone: '(555) 123-4567',
  date: '2023-11-15',
  time: '10:00 AM',
  message: 'I would like to see the property this weekend if possible.',
  status: 'pending'
}, {
  id: '2',
  listingId: '1',
  userId: 'u2',
  userName: 'Bob Smith',
  userEmail: 'bob@example.com',
  userPhone: '(555) 987-6543',
  date: '2023-11-16',
  time: '3:30 PM',
  message: 'Interested in seeing the backyard and garage specifically.',
  status: 'accepted'
}, {
  id: '3',
  listingId: '3',
  userId: 'u3',
  userName: 'Carol Williams',
  userEmail: 'carol@example.com',
  userPhone: '(555) 456-7890',
  date: '2023-11-18',
  time: '1:00 PM',
  message: '',
  status: 'rejected'
}];
export const mockListings: Listing[] = [{
  id: '1',
  title: 'Modern Family Home',
  description: "Beautiful modern home with spacious rooms and a large backyard. Perfect for families looking for comfort and style. This property features high ceilings, hardwood floors, and an open floor plan that's ideal for entertaining. The kitchen has been recently renovated with high-end appliances.",
  price: 450000,
  location: '123 Main St, Anytown, USA',
  type: 'house',
  listingType: 'sale',
  size: 2500,
  bedrooms: 4,
  bathrooms: 3,
  images: ['https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1576941089067-2de3c901e126?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1578&q=80'],
  documents: [{
    title: 'Property Deed',
    url: '#'
  }, {
    title: 'Tax Records',
    url: '#'
  }, {
    title: 'Floor Plans',
    url: '#'
  }],
  owner: {
    id: '101',
    name: 'John Smith',
    email: 'john@example.com',
    phone: '(123) 456-7890',
    companyName: 'Prime Real Estate Agency',
    companyNIF: '123456789',
    companyLogo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  },
  featured: true,
  createdAt: '2023-05-15T10:30:00Z',
  comments: [{
    id: 'c1',
    userId: 'u1',
    userName: 'Alice Johnson',
    rating: 5,
    comment: 'Beautiful property! The location is perfect and the house is in excellent condition. Highly recommend viewing this one.',
    createdAt: '2023-11-10T14:30:00Z'
  }, {
    id: 'c2',
    userId: 'u2',
    userName: 'Bob Williams',
    rating: 4,
    comment: 'Great house with lots of space. The backyard is amazing for kids. Only minor issue is the distance to the nearest school.',
    createdAt: '2023-11-12T09:15:00Z'
  }],
  averageRating: 4.5,
  totalRatings: 2,
  status: 'approved',
  reviewedAt: '2023-05-16T08:00:00Z',
  reviewedBy: 'Admin User'
}, {
  id: '2',
  title: 'Downtown Apartment',
  description: 'Luxury apartment in the heart of downtown. Walking distance to restaurants, shops, and entertainment. This recently renovated unit features stainless steel appliances, granite countertops, and a private balcony with city views. Building amenities include a fitness center and rooftop pool.',
  price: 2500,
  location: '456 Park Ave, Anytown, USA',
  type: 'house',
  listingType: 'rent',
  rentPeriod: 'monthly',
  size: 1200,
  bedrooms: 2,
  bathrooms: 2,
  images: ['https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1380&q=80', 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1474&q=80'],
  documents: [{
    title: 'Condo Documents',
    url: '#'
  }, {
    title: 'HOA Information',
    url: '#'
  }, {
    title: 'Floor Plans',
    url: '#'
  }],
  owner: {
    id: '102',
    name: 'Sarah Johnson',
    email: 'sarah@example.com',
    phone: '(123) 456-7891'
  },
  createdAt: '2023-06-20T14:45:00Z',
  comments: [{
    id: 'c3',
    userId: 'u3',
    userName: 'Carol Martinez',
    rating: 5,
    comment: 'Perfect downtown location! The apartment is modern and well-maintained. Building amenities are top-notch.',
    createdAt: '2023-11-08T16:20:00Z'
  }],
  averageRating: 5,
  totalRatings: 1,
  status: 'approved',
  reviewedAt: '2023-06-21T09:30:00Z',
  reviewedBy: 'Admin User'
}, {
  id: '3',
  title: 'Rural Land Parcel',
  description: '10-acre land parcel with beautiful views and development potential. This property features rolling hills, mature trees, and a small stream. Perfect for building your dream home or for recreational use. The land has been surveyed and has road access. Utilities are available at the property line.',
  price: 150000,
  location: 'Rural Route 7, Countryside, USA',
  type: 'land',
  listingType: 'sale',
  size: 43560 * 10,
  images: ['https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1632&q=80', 'https://images.unsplash.com/photo-1628624747186-a941c476b7ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1543051932-6ef9fecfbc1a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1501084291732-13b1ba8f0ebc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
  documents: [{
    title: 'Land Survey',
    url: '#'
  }, {
    title: 'Soil Test Results',
    url: '#'
  }, {
    title: 'Property Boundaries',
    url: '#'
  }, {
    title: 'Zoning Information',
    url: '#'
  }],
  owner: {
    id: '103',
    name: 'Michael Brown',
    email: 'michael@example.com',
    phone: '(123) 456-7892'
  },
  featured: true,
  createdAt: '2023-07-05T09:15:00Z',
  comments: [],
  averageRating: 0,
  totalRatings: 0,
  status: 'approved',
  reviewedAt: '2023-07-06T10:00:00Z',
  reviewedBy: 'Admin User'
}, {
  id: '4',
  title: 'Beachfront Property',
  description: 'Stunning beachfront property with panoramic ocean views. This exclusive property offers direct beach access and a spacious outdoor living area. The home features floor-to-ceiling windows, a gourmet kitchen, and a master suite with ocean views. Perfect for those seeking luxury coastal living.',
  price: 1200000,
  location: '789 Ocean Drive, Beachtown, USA',
  type: 'house',
  listingType: 'sale',
  size: 3200,
  bedrooms: 5,
  bathrooms: 4,
  images: ['https://images.unsplash.com/photo-1527030280862-64139fba04ca?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1506&q=80', 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1591825729269-caeb344f6df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1474&q=80'],
  documents: [{
    title: 'Property Deed',
    url: '#'
  }, {
    title: 'Coastal Zone Permits',
    url: '#'
  }, {
    title: 'Floor Plans',
    url: '#'
  }, {
    title: 'Insurance Information',
    url: '#'
  }],
  owner: {
    id: '104',
    name: 'Jennifer Davis',
    email: 'jennifer@example.com',
    phone: '(123) 456-7893'
  },
  createdAt: '2023-08-12T16:20:00Z',
  comments: [{
    id: 'c4',
    userId: 'u4',
    userName: 'David Chen',
    rating: 5,
    comment: 'Absolutely stunning property! The ocean views are breathtaking and the location is unbeatable.',
    createdAt: '2023-11-05T11:45:00Z'
  }, {
    id: 'c5',
    userId: 'u5',
    userName: 'Emma Wilson',
    rating: 5,
    comment: 'Dream home! Everything about this property is perfect. Worth every penny.',
    createdAt: '2023-11-07T13:30:00Z'
  }],
  averageRating: 5,
  totalRatings: 2,
  status: 'approved',
  reviewedAt: '2023-08-13T08:45:00Z',
  reviewedBy: 'Admin User'
}, {
  id: '5',
  title: 'Commercial Land',
  description: 'Prime commercial land in a rapidly developing area. This property is zoned for commercial use and is located near major highways with excellent visibility. Perfect for retail, office, or mixed-use development. The site has been cleared and is ready for construction with all utilities available.',
  price: 500000,
  location: '101 Business Pkwy, Commerce City, USA',
  type: 'land',
  listingType: 'sale',
  size: 87120,
  images: ['https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1596&q=80', 'https://images.unsplash.com/photo-1486325212027-8081e485255e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1504823090713-cb8d8a419676?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1582376432754-b63cc6a9b8c3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
  documents: [{
    title: 'Zoning Certificate',
    url: '#'
  }, {
    title: 'Environmental Assessment',
    url: '#'
  }, {
    title: 'Traffic Study',
    url: '#'
  }, {
    title: 'Development Plans',
    url: '#'
  }],
  owner: {
    id: '105',
    name: 'Robert Wilson',
    email: 'robert@example.com',
    phone: '(123) 456-7894'
  },
  featured: true,
  createdAt: '2023-09-03T11:10:00Z',
  comments: [],
  averageRating: 0,
  totalRatings: 0,
  status: 'approved',
  reviewedAt: '2023-09-04T09:20:00Z',
  reviewedBy: 'Admin User'
}, {
  id: '6',
  title: 'Mountain Cabin',
  description: 'Cozy mountain cabin with stunning views. This rustic retreat features a stone fireplace, exposed wood beams, and a wraparound deck. Perfect for weekend getaways or as a vacation rental. The property includes access to hiking trails and is just minutes from skiing and other outdoor activities.',
  price: 275000,
  location: '456 Mountain View Rd, Highland, USA',
  type: 'house',
  listingType: 'sale',
  size: 1800,
  bedrooms: 3,
  bathrooms: 2,
  images: ['https://images.unsplash.com/photo-1518780664697-55e3ad937233?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1530&q=80', 'https://images.unsplash.com/photo-1464146072230-91cabc968266?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1542718610-a1d656d1884c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1470770841072-f978cf4d019e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
  documents: [{
    title: 'Property Deed',
    url: '#'
  }, {
    title: 'Septic System Report',
    url: '#'
  }, {
    title: 'Well Water Test',
    url: '#'
  }, {
    title: 'Boundary Survey',
    url: '#'
  }],
  owner: {
    id: '106',
    name: 'Emily Thompson',
    email: 'emily@example.com',
    phone: '(123) 456-7895'
  },
  createdAt: '2023-10-18T13:40:00Z',
  comments: [{
    id: 'c6',
    userId: 'u6',
    userName: 'Frank Anderson',
    rating: 4,
    comment: 'Charming cabin with great mountain views. Perfect for a weekend retreat. Would love to see more photos of the interior.',
    createdAt: '2023-11-09T10:20:00Z'
  }],
  averageRating: 4,
  totalRatings: 1,
  status: 'approved',
  reviewedAt: '2023-10-19T08:15:00Z',
  reviewedBy: 'Admin User'
}, {
  id: '7',
  title: 'Suburban Family Home',
  description: 'Spacious family home in quiet suburban neighborhood. Features include updated kitchen, large backyard, and attached two-car garage. Close to schools and shopping centers.',
  price: 385000,
  location: '789 Maple Street, Suburbia, USA',
  type: 'house',
  listingType: 'sale',
  size: 2200,
  bedrooms: 4,
  bathrooms: 2,
  images: ['https://images.unsplash.com/photo-1568605114967-8130f3a36994?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80'],
  documents: [{
    title: 'Property Deed',
    url: '#'
  }],
  owner: {
    id: '101',
    name: 'John Smith',
    email: 'john@example.com',
    phone: '(123) 456-7890',
    companyName: 'Prime Real Estate Agency',
    companyNIF: '123456789',
    companyLogo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  },
  featured: false,
  createdAt: '2023-11-20T10:30:00Z',
  comments: [],
  averageRating: 0,
  totalRatings: 0,
  status: 'pending'
}, {
  id: '8',
  title: 'Luxury Penthouse',
  description: 'Exclusive penthouse with panoramic city views. Features high-end finishes, private elevator access, and rooftop terrace. Building amenities include concierge, gym, and pool.',
  price: 8500,
  location: '100 Skyline Ave, Metro City, USA',
  type: 'house',
  listingType: 'rent',
  rentPeriod: 'monthly',
  size: 2800,
  bedrooms: 3,
  bathrooms: 3,
  images: ['https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80'],
  documents: [{
    title: 'Lease Agreement Template',
    url: '#'
  }],
  owner: {
    id: '102',
    name: 'Sarah Johnson',
    email: 'sarah@example.com',
    phone: '(123) 456-7891'
  },
  featured: false,
  createdAt: '2023-11-21T14:20:00Z',
  comments: [],
  averageRating: 0,
  totalRatings: 0,
  status: 'rejected',
  rejectionReason: 'Incomplete documentation. Please provide proof of ownership and building permits.',
  reviewedAt: '2023-11-22T09:00:00Z',
  reviewedBy: 'Admin User'
}];
// Mock user data with license information
export const currentUser = {
  id: '101',
  name: 'John Smith',
  email: 'john@example.com',
  phone: '(123) 456-7890',
  role: 'owner' as UserRole,
  companyName: 'Prime Real Estate Agency',
  companyNIF: '123456789',
  companyLogo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  license: {
    licenseId: 'starter',
    purchaseDate: '2024-01-01T00:00:00Z',
    expiryDate: '2024-01-31T23:59:59Z',
    usedListings: 3,
    maxListings: 5,
    isActive: true
  } as UserLicense
};